import React, { useState } from 'react'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import { styled, SxProps, Theme } from '@mui/material/styles'
import { FormHelperText } from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import PasswordLockIcon from '@/assets/PasswordLockIcon.svg'

interface PasswordInputPropsType {
  type?: string
  value: string | boolean
  label: string | number
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  sx?: SxProps<Theme>
  className?: string
  error?: boolean
  helperText?: string
}

const CustomInputField = styled(TextField)<{ value: string | boolean }>(
  ({ theme, value }) => ({
    minHeight: '80px',
    minWidth: '100px',
    marginRight: '16px',
    color: '#7D7D7D',
    padding: '1.2px',
    paddingTop: '3px',
    borderTop: '1.2px solid rgba(54, 94, 145, 1)',
    backgroundColor: 'rgb(8,12,20,0.05)',
    borderColor: '#365F91',
    border: '1.2px solid transparent',
    background: `
      linear-gradient(rgb(8, 12, 20), rgb(8, 12, 20)) padding-box,
      linear-gradient(180deg, #365f91 30%, #365f9133 100%) border-box
    `,
    borderImageSlice: 1,
    borderRadius: '40px',
    '& .MuiInputBase-input': {
      color: 'white',
      fontSize: '18px',
      paddingTop: value ? '35px' : 'auto',
    },
    '& .MuiInputLabel-root': {
      color: '#7D7D7D !important',
      left: '46.5px',
      paddingTop: '7px',
      transform: 'translate(0, +23px)',
      paddingLeft: '8px',
    },
    '& .MuiInputLabel-shrink': {
      left: '46.5px',
      color: '#7D7D7D !important',
      fontSize: '12px',
      transform: 'translate(0, -1px)',
      paddingTop: value ? '20px' : '8px',
      paddingLeft: '8px',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: 'white !important',
    },
    '& .MuiFilledInput-root': {
      backgroundColor: 'transparent',
      '&:before': {
        borderBottom: 'none !important',
        backgroundColor: 'transparent !important',
      },
      '&:after': {
        borderBottom: 'none !important',
        backgroundColor: 'transparent !important',
      },
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '&.Mui-focused:after': {
        color: '#7D7D7D',
        borderBottom: 'none',
      },
    },
    '&::placeholder': {
      color: '#7D7D7D',
      opacity: 1,
    },
    [theme.breakpoints.down('md')]: {
      width: '45vw',
    },
    [theme.breakpoints.down('sm')]: {
      width: '80vw',
    },
  }),
)

const PasswordInput = ({
  value,
  label,
  onChange,
  placeholder,
  error,
  helperText,
  ...props
}: PasswordInputPropsType) => {
  const [showPassword, setShowPassword] = useState<boolean>(false)

  const handleClickShowPassword = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword)
  }

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault()
  }

  return (
    <div className="relative">
      <CustomInputField
        error={!!error}
        type={showPassword ? 'text' : 'password'}
        label={label}
        variant="filled"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{
                marginBottom: value ? '9px !important' : '0px !important',
                paddingLeft: '10px',
              }}
            >
              <img
                src={PasswordLockIcon}
                alt="Password lock icon"
                className="text-white !h-6 !w-7"
              />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment
              position="end"
              sx={{
                marginBottom: value ? '9px !important' : '0 !important',
              }}
            >
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
                sx={{ color: 'white' }}
              >
                {showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        InputLabelProps={{
          shrink: value ? true : false,
        }}
        {...props}
      />
      <FormHelperText
        error
        sx={{
          height: '6px',
          marginLeft: '14px',
          marginTop: '4px',
          color: '#f44336',
        }}
      >
        {error && helperText && helperText}
      </FormHelperText>
    </div>
  )
}

export default PasswordInput
